import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { areasAPI, camerasAPI, modelsAPI, logsAPI } from '../../utils/api';
import { useAuthenticatedBlobUrl } from '../../utils/useAuthenticatedBlobUrl';
// import { usePeopleCountWebSocket } from '../../utils/usePeopleCountWebSocket';
import {
  cameraHasPeopleCounterModel,
  isPeopleCounterModel,
} from '../../utils/peopleCounter';
import Loader from '../../components/Loader/Loader';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import RoiSelector from '../../components/RoiSelector/RoiSelector';
import PresenceSettingsModal from '../../components/PresenceSettingsModal/PresenceSettingsModal';
import {
  cameraHasPresenceCheckModel,
  DEFAULT_PRESENCE_SETTINGS,
  isPresenceCheckModel,
  normalizePresenceSettings,
  PresenceZone,
} from '../../utils/presence';
import LogsDateRangePicker from '../../components/LogsDateRangePicker/LogsDateRangePicker';
import DetectionsStatsModal from '../../components/DetectionsStatsModal/DetectionsStatsModal';
import { getModelDisplayName } from '../../utils/modelLabels';
import { isSubscriptionRequiredError } from '../../utils/subscriptionErrors';
import './Area.css';

interface AreaData {
  id: number;
  name: string;
  description: string | null;
}

// Нормализация ключа модели для фильтра детекций (в логах могут быть другие алиасы).
// Пример: на камере "people", а в логах приходит "person".
const normalizeLogModelKey = (modelName: string): string => {
  const key = (modelName || '').trim().toLowerCase();
  const alias: Record<string, string> = {
    people: 'person',
    person: 'person',
    vehicle: 'car',
    car: 'car',
    bus: 'bus',
    truck: 'truck',
    police: 'police',
    ambulance: 'ambulance',
    firetruck: 'firetruck',
    fire: 'fire',
    smoke: 'smoke',
    garbage_truck: 'garbage_truck',
    garbage_truck_detector: 'garbage_truck',
    lpd: 'lpd',
    license_plate_recognition: 'lpd',
    tractor: 'tractor',
    tractor_detector: 'tractor',
    smoking: 'smoking',
    smoking_detector: 'smoking',
    cigarette: 'smoking',
    emergency_car_detector: 'emergency_car_detector',
    multiple_rtsp: 'multiple_rtsp',
  };
  return alias[key] || key;
};

type AreaLogItem = {
  id: number;
  timestamp: string;
  model: string;
  screen_url: string | null;
  json_data: any;
  camera_id?: number | null;
};

/** Обновляет первую страницу логов, сохраняя подгруженные через «Показать ещё». */
const mergeFreshLogsHead = (
  prev: AreaLogItem[],
  fresh: AreaLogItem[],
  firstPageSize: number
): AreaLogItem[] => {
  if (prev.length <= firstPageSize) {
    return fresh;
  }
  return [...fresh, ...prev.slice(firstPageSize)];
};

const Area: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading: authLoading } = useAuth();
  const cameraSimUrl = `${process.env.PUBLIC_URL}/icons/area/camera.png`;
  const cameraIconSvgUrl = `${process.env.PUBLIC_URL}/icons/svg_icon/camera.svg`;
  const roiIconSvgUrl = `${process.env.PUBLIC_URL}/icons/svg_icon/roi.svg`;
  const presenceIconSvgUrl = `${process.env.PUBLIC_URL}/icons/modals/people.svg`;
  const modalIconsBase = `${process.env.PUBLIC_URL}/icons/modals`;

  const isForbiddenOrNotFound = React.useCallback((err: any) => {
    if (isSubscriptionRequiredError(err)) {
      return false;
    }
    const status = err?.status ?? err?.response?.status;
    return status === 403 || status === 404;
  }, []);

  const handleSubscriptionLost = React.useCallback(() => {
    navigate('/dashboard', { replace: true });
  }, [navigate]);

  const handleApiAccessError = React.useCallback(
    (err: unknown): boolean => {
      if (isSubscriptionRequiredError(err)) {
        handleSubscriptionLost();
        return true;
      }
      if (isForbiddenOrNotFound(err)) {
        navigate('/dashboard', { replace: true });
        return true;
      }
      return false;
    },
    [handleSubscriptionLost, isForbiddenOrNotFound, navigate]
  );

  const getCameraIdentifier = React.useCallback(
    (c: { id: number | null; camera_id: string | null; name: string }) => String(c.id ?? c.camera_id ?? c.name),
    []
  );

  const [area, setArea] = useState<AreaData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [cameras, setCameras] = useState<Array<{ 
    id: number | null; 
    name: string; 
    rtsp_uri: string; 
    models: string[]; 
    camera_id: string | null;
    hls_out?: string | null;
    webrtc_out?: string | null;
    status?: any;
  }>>([]);
  const [camerasLoading, setCamerasLoading] = useState(false);
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [logs, setLogs] = useState<AreaLogItem[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsLoadingMore, setLogsLoadingMore] = useState(false);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPerPage] = useState(10); // Первая загрузка - 10 логов
  const [logsPerPageMore] = useState(20); // При "показать еще" - 20 логов
  /** null — все камеры участка; иначе id камеры в БД */
  const [logsCameraFilterId, setLogsCameraFilterId] = useState<number | null>(null);
  /** null — все модели; иначе значение `logs.model` */
  const [logsModelFilter, setLogsModelFilter] = useState<string | null>(null);
  /** YYYY-MM-DD; пустая строка — без границы */
  const [logsDateFrom, setLogsDateFrom] = useState('');
  const [logsDateTo, setLogsDateTo] = useState('');
  const [logsSort, setLogsSort] = useState<{ key: 'timestamp' | 'model'; dir: 'asc' | 'desc' }>({
    key: 'timestamp',
    dir: 'desc',
  });
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [selectedPhotoUrl, setSelectedPhotoUrl] = useState<string | null>(null);
  const {
    blobUrl: selectedPhotoBlobUrl,
    loading: selectedPhotoLoading,
    error: selectedPhotoError,
    onImageError: onSelectedPhotoImageError,
  } = useAuthenticatedBlobUrl(selectedPhotoUrl, isPhotoModalOpen);

  const [logsModelsLoading, setLogsModelsLoading] = useState(false);
  const [logsModels, setLogsModels] = useState<string[]>([]);
  const [isDetectionsStatsOpen, setIsDetectionsStatsOpen] = useState(false);

  const deepLinkOpenedRef = useRef(false);

  const deepLinkLogId = useMemo(() => {
    const sp = new URLSearchParams(location.search || '');
    const raw = sp.get('log') || sp.get('detection') || sp.get('log_id');
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [location.search]);

  useEffect(() => {
    deepLinkOpenedRef.current = false;
  }, [deepLinkLogId]);

  useEffect(() => {
    if (deepLinkOpenedRef.current) return;
    if (!deepLinkLogId || !area?.id) return;

    const openLogModal = (item: { id: number; screen_url: string | null }) => {
      deepLinkOpenedRef.current = true;
      if (item.screen_url) {
        setSelectedPhotoUrl(item.screen_url);
        setIsPhotoModalOpen(true);
      }
    };

    const inList = logs.find((l) => l.id === deepLinkLogId);
    if (inList) {
      openLogModal(inList);
      return;
    }

    if (logsLoading) return;

    let cancelled = false;
    (async () => {
      try {
        const resp = await logsAPI.getById(area.id, deepLinkLogId);
        if (cancelled || !resp.success || !resp.log) return;

        setLogs((prev) => {
          if (prev.some((l) => l.id === resp.log!.id)) return prev;
          return [resp.log!, ...prev];
        });
        openLogModal(resp.log);
      } catch {
        // ignore: лог мог быть удалён или недоступен
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [deepLinkLogId, logs, logsLoading, area?.id]);

  useEffect(() => {
    const sp = new URLSearchParams(location.search || '');
    if (sp.get('openStats') === '1' && area?.id) {
      setIsDetectionsStatsOpen(true);
    }
  }, [location.search, area?.id]);

  /** Список моделей для фильтра — после sync логов в БД, иначе выпадающий список устаревает. */
  const loadLogsModels = React.useCallback(async () => {
    if (!area?.id) return;

    try {
      setLogsModelsLoading(true);
      const resp = await logsAPI.getModelsByArea(area.id, {
        ...(logsCameraFilterId != null ? { cameraId: logsCameraFilterId } : {}),
        ...(logsDateFrom ? { dateFrom: logsDateFrom } : {}),
        ...(logsDateTo ? { dateTo: logsDateTo } : {}),
      });
      if (resp.success) {
        setLogsModels(resp.models || []);
      }
    } catch {
      // ignore: не блокируем страницу
    } finally {
      setLogsModelsLoading(false);
    }
  }, [area?.id, logsCameraFilterId, logsDateFrom, logsDateTo]);

  const modelOptionsForArea = useMemo(() => {
    const keys = new Set<string>();
    logsModels.forEach((m) => {
      const k = normalizeLogModelKey(m);
      if (k) keys.add(k);
    });
    logs.forEach((l) => {
      const k = normalizeLogModelKey(l.model);
      if (k) keys.add(k);
    });
    return Array.from(keys)
      .sort((a, b) => getModelDisplayName(a).localeCompare(getModelDisplayName(b), 'ru', { sensitivity: 'base' }))
      .map((value) => ({ value, label: getModelDisplayName(value) }));
  }, [logsModels, logs]);

  useEffect(() => {
    if (logsModelFilter == null) return;
    const ok = modelOptionsForArea.some((o) => o.value === logsModelFilter);
    if (!ok) setLogsModelFilter(null);
  }, [logsModelFilter, modelOptionsForArea]);

  const sortedLogs = useMemo(() => {
    const dirMul = logsSort.dir === 'asc' ? 1 : -1;
    const byTime = (a: string, b: string) => (a === b ? 0 : a < b ? -1 : 1);
    const byText = (a: string, b: string) => a.localeCompare(b, 'ru', { sensitivity: 'base' });

    return [...logs].sort((a, b) => {
      if (logsSort.key === 'timestamp') {
        const cmp = byTime(a.timestamp, b.timestamp) * dirMul;
        if (cmp !== 0) return cmp;
        return byText(getModelDisplayName(a.model), getModelDisplayName(b.model)) * dirMul;
      }

      const cmp = byText(getModelDisplayName(a.model), getModelDisplayName(b.model)) * dirMul;
      if (cmp !== 0) return cmp;
      return byTime(a.timestamp, b.timestamp) * dirMul;
    });
  }, [logs, logsSort]);

  const toggleLogsSort = (key: 'timestamp' | 'model') => {
    setLogsSort((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    });
  };

  // Загрузка информации об участке
  useEffect(() => {
    // Ждем завершения авторизации перед загрузкой данных участка
    if (authLoading) {
      return;
    }

    const loadArea = async () => {
      if (!id) {
        navigate('/dashboard');
        return;
      }

      try {
        setLoading(true);
        const response = await areasAPI.getOne(Number(id));
        if (response.success && response.area) {
          setArea(response.area);
        } else {
          navigate('/dashboard', { replace: true });
        }
      } catch (err: any) {
        if (handleApiAccessError(err)) return;
        setError(err.message || 'Ошибка загрузки участка');
      } finally {
        setLoading(false);
      }
    };

    loadArea();
  }, [id, navigate, authLoading, handleApiAccessError]);

  useEffect(() => {
    setLogsCameraFilterId(null);
    setLogsModelFilter(null);
    setLogsDateFrom('');
    setLogsDateTo('');
  }, [id]);

  useEffect(() => {
    if (logsCameraFilterId == null) return;
    const stillThere = cameras.some((c) => c.id === logsCameraFilterId);
    if (!stillThere) setLogsCameraFilterId(null);
  }, [cameras, logsCameraFilterId]);

  // Загрузка камер для участка
  const loadCameras = React.useCallback(async () => {
    if (!area || !id) return;

    try {
      setCamerasLoading(true);
      console.log('Loading cameras for area:', id);
      const response = await camerasAPI.getByArea(Number(id));
      console.log('Cameras response:', response);
      if (response.success) {
        const nextCameras = response.cameras || [];
        setCameras(nextCameras);
        console.log('Cameras loaded:', response.cameras);

        // Если активная камера была удалена/изменилась — выбираем первую доступную,
        // иначе UI может показывать "дубли" (главная камера + та же в миниатюрах).
        setActiveCameraId((prev) => {
          if (nextCameras.length === 0) return null;
          const ids = new Set(nextCameras.map(getCameraIdentifier));
          if (prev === null) return getCameraIdentifier(nextCameras[0]);
          return ids.has(String(prev)) ? prev : getCameraIdentifier(nextCameras[0]);
        });
      } else {
        console.error('Failed to load cameras:', response);
      }
    } catch (err: any) {
      if (handleApiAccessError(err)) return;
      console.error('Ошибка загрузки камер:', err);
    } finally {
      setCamerasLoading(false);
    }
  }, [area, id, getCameraIdentifier, handleApiAccessError]);

  useEffect(() => {
    loadCameras();
  }, [loadCameras]);

  // Загрузка доступных моделей (аналитик)
  useEffect(() => {
    const loadModels = async () => {
      try {
        setModelsLoading(true);
        const response = await modelsAPI.getAll();
        if (response.success) {
          setAvailableModels(response.models);
        }
      } catch (err: any) {
        console.error('Ошибка загрузки моделей:', err);
      } finally {
        setModelsLoading(false);
      }
    };

    loadModels();
  }, []);

  const getLogsListOptions = React.useCallback(
    (extra?: { sync?: boolean; extMax?: number }): NonNullable<Parameters<typeof logsAPI.getByArea>[3]> => ({
      ...extra,
      ...(logsCameraFilterId != null ? { cameraId: logsCameraFilterId } : {}),
      ...(logsModelFilter != null ? { model: logsModelFilter } : {}),
      ...(logsDateFrom ? { dateFrom: logsDateFrom } : {}),
      ...(logsDateTo ? { dateTo: logsDateTo } : {}),
    }),
    [logsCameraFilterId, logsModelFilter, logsDateFrom, logsDateTo]
  );

  /** Тихая подкачка из внешнего API (не блокирует UI). */
  const syncLogsFromExternal = React.useCallback(async () => {
    if (!area?.id) return;

    const now = Date.now();
    const last = (window as any).__logsLastSyncAt as number | undefined;
    const syncCooldownMs = 15_000;
    if (last && now - last < syncCooldownMs) {
      return;
    }

    try {
      const response = await logsAPI.getByArea(
        area.id,
        1,
        logsPerPage,
        getLogsListOptions({ sync: true, extMax: 200 })
      );
      if (!response.success) return;

      (window as any).__logsLastSyncAt = now;
      const fresh = response.logs || [];
      setLogsTotal(response.total || 0);
      setLogs((prev) => mergeFreshLogsHead(prev, fresh, logsPerPage));
      await loadLogsModels();
    } catch (err) {
      handleApiAccessError(err);
    }
  }, [area?.id, logsPerPage, getLogsListOptions, loadLogsModels, handleApiAccessError]);

  // Загрузка первых 10 логов участка — сначала быстро из БД (как «Показать ещё»), sync в фоне
  const loadLogs = React.useCallback(async () => {
    if (!area?.id) {
      return;
    }

    try {
      setLogsLoading(true);
      const response = await logsAPI.getByArea(
        area.id,
        1,
        logsPerPage,
        getLogsListOptions({ sync: false })
      );
      if (response.success) {
        setLogs(response.logs || []);
        setLogsTotal(response.total || 0);
      } else {
        setLogs([]);
        setLogsTotal(0);
      }
    } catch (err: any) {
      if (handleApiAccessError(err)) return;
      setLogs([]);
      setLogsTotal(0);
    } finally {
      setLogsLoading(false);
    }

    void loadLogsModels();
    void syncLogsFromExternal();
  }, [
    area?.id,
    logsPerPage,
    getLogsListOptions,
    loadLogsModels,
    syncLogsFromExternal,
    handleApiAccessError,
  ]);

  // Тихое обновление логов (без сброса "показать еще")
  const refreshLogs = React.useCallback(async () => {
    if (!area?.id) return;

    const now = Date.now();
    const last = (window as any).__logsLastSyncAt as number | undefined;
    const syncCooldownMs = 15_000;
    const shouldSync = !last || now - last >= syncCooldownMs;

    try {
      const response = await logsAPI.getByArea(
        area.id,
        1,
        logsPerPage,
        getLogsListOptions({ sync: false })
      );
      if (!response.success) return;

      const fresh = response.logs || [];
      setLogsTotal(response.total || 0);
      setLogs((prev) => mergeFreshLogsHead(prev, fresh, logsPerPage));

      if (shouldSync) {
        void syncLogsFromExternal();
      }
    } catch (err) {
      handleApiAccessError(err);
    }
  }, [area?.id, logsPerPage, getLogsListOptions, syncLogsFromExternal, handleApiAccessError]);

  // Загрузка дополнительных логов (по 20)
  const loadMoreLogs = React.useCallback(async () => {
    if (!area?.id || logsLoadingMore) return;

    const currentLoaded = logs.length;
    
    let nextPage: number;
    let perPage: number;
    let skipFirst: number = 0;
    
    if (currentLoaded === logsPerPage) {
      nextPage = 1;
      perPage = logsPerPageMore;
      skipFirst = logsPerPage;
    } else {
      // Для последующих загрузок рассчитываем страницу на основе per_page=20
      // Если загружено 30 элементов (10 + 20), то следующая страница = 2
      nextPage = Math.floor((currentLoaded - logsPerPage) / logsPerPageMore) + 1;
      perPage = logsPerPageMore;
      skipFirst = 0;
    }

    try {
      setLogsLoadingMore(true);
      console.log('Loading more logs for area:', area.id, 'page:', nextPage, 'per_page:', perPage, 'current loaded:', currentLoaded, 'skip first:', skipFirst);
      // Подгрузка "Показать ещё": НЕ запускаем sync на бэке, иначе подгрузка будет очень медленной.
      const response = await logsAPI.getByArea(area.id, nextPage, perPage, getLogsListOptions({ sync: false }));
      console.log('More logs response:', response);
      if (response.success && response.logs && response.logs.length > 0) {
        // Пропускаем первые skipFirst элементов, если нужно
        const logsToAdd = skipFirst > 0 ? response.logs.slice(skipFirst) : response.logs;
        
        if (logsToAdd.length > 0) {
          console.log('More logs loaded:', logsToAdd.length);
          setLogs(prevLogs => [...prevLogs, ...logsToAdd]);
        } else {
          console.log('No more logs to load (all filtered)');
        }
      } else {
        console.log('No more logs to load');
      }
    } catch (err: any) {
      if (handleApiAccessError(err)) return;
      console.error('Ошибка загрузки дополнительных логов:', err);
    } finally {
      setLogsLoadingMore(false);
    }
  }, [area?.id, logs.length, logsLoadingMore, logsPerPage, logsPerPageMore, getLogsListOptions, handleApiAccessError]);

  useEffect(() => {
    if (area) {
      loadLogs();
    }
  }, [area, loadLogs]);

  // Фоновая подгрузка логов без перезагрузки страницы
  useEffect(() => {
    if (!area?.id) return;

    const intervalId = window.setInterval(() => {
      refreshLogs();
    }, 15000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [area?.id, refreshLogs]);

  const [activeCameraId, setActiveCameraId] = useState<string | null>(null);
  const [isDeleteCameraOpen, setIsDeleteCameraOpen] = useState(false);
  const [videoReloadToken, setVideoReloadToken] = useState(0);
  const [isRoiSelectorOpen, setIsRoiSelectorOpen] = useState(false);
  const [isPresenceSettingsOpen, setIsPresenceSettingsOpen] = useState(false);
  const [presenceMonitoringEnabled, setPresenceMonitoringEnabled] = useState(false);
  const [presenceZone, setPresenceZone] = useState<PresenceZone | null>(null);
  const mainVideoRef = useRef<HTMLDivElement>(null);
  const mainIframeRef = useRef<HTMLIFrameElement | null>(null);
  const roiOverlayCanvasRef = useRef<HTMLCanvasElement>(null);

  // Устанавливаем первую камеру как активную при загрузке
  useEffect(() => {
    if (cameras.length > 0 && activeCameraId === null) {
      setActiveCameraId(getCameraIdentifier(cameras[0]));
    }
  }, [cameras, activeCameraId, getCameraIdentifier]);

  const activeCamera = cameras.find((c) => getCameraIdentifier(c) === String(activeCameraId)) ?? cameras[0] ?? null;

  /** Модели, реально привязанные к камере (БД). Не путать с `status.models` внешнего API — там зона ROI может ещё долго «висеть» после detach. */
  const attachedCameraModels = activeCamera?.models ?? [];
  // const activeHasPeopleModel = cameraHasPeopleCounterModel(attachedCameraModels);
  const activeHasPresenceModel = cameraHasPresenceCheckModel(attachedCameraModels);
  // const [peopleCounterLiveEnabled, setPeopleCounterLiveEnabled] = useState(false);

  // Счётчик людей временно отключён (PEOPLE_COUNTER_ENABLED = false).
  // const showPeopleCounterPanel = Boolean(
  //   activeHasPeopleModel && activeCamera?.camera_id && peopleCounterLiveEnabled
  // );
  // const {
  //   count: peopleCount,
  //   connected: peopleWsConnected,
  //   error: peopleWsError,
  // } = usePeopleCountWebSocket(activeCamera?.camera_id ?? null, showPeopleCounterPanel);

  // Получаем размеры видео из статуса камеры
  const getVideoDimensions = () => {
    if (!activeCamera?.status) return { width: 1920, height: 1080 };

    const status = activeCamera.status;
    if (status.resolution?.width && status.resolution?.height) {
      return { width: status.resolution.width, height: status.resolution.height };
    } else if (status.src_width && status.src_height) {
      return { width: status.src_width, height: status.src_height };
    } else if (status.width && status.height) {
      return { width: status.width, height: status.height };
    }
    return { width: 1920, height: 1080 };
  };

  const videoDimensions = getVideoDimensions();

  // Отрисовка ROI оверлея на основном видео
  useEffect(() => {
    const canvas = roiOverlayCanvasRef.current;
    const container = mainVideoRef.current;
    if (!canvas || !container) return;

    // Всегда очищаем canvas при смене камеры/статуса — иначе может оставаться оверлей от предыдущей камеры,
    // когда у текущей еще нет roi данных.
    const clear = () => {
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    clear();

    const roiData = activeCamera?.status?.roi;
    const boundModelSet = new Set(attachedCameraModels);
    const roiModelKeys =
      roiData && Object.keys(roiData).length > 0
        ? Object.keys(roiData).filter((modelName) => boundModelSet.has(modelName))
        : [];

    const hasPresenceZone = Boolean(presenceMonitoringEnabled && presenceZone);

    if (roiModelKeys.length === 0 && !hasPresenceZone) {
      return;
    }

    const updateCanvasSize = () => {
      const iframe = container.querySelector('iframe');
      if (iframe) {
        const rect = iframe.getBoundingClientRect();
        canvas.width = rect.width;
        canvas.height = rect.height;
        canvas.style.width = rect.width + 'px';
        canvas.style.height = rect.height + 'px';
        drawRoiOverlay();
      } else {
        // если iframe еще не появился/переинициализируется — не оставляем старый рисунок
        clear();
      }
    };

    const drawRoiOverlay = () => {
      const ctx = canvas.getContext('2d');
      if (!ctx || canvas.width === 0 || canvas.height === 0) return;

      // Очищаем canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Вычисляем реальную область видео с учетом соотношения сторон
      const videoAspect = videoDimensions.width / videoDimensions.height;
      const canvasAspect = canvas.width / canvas.height;

      let videoDisplayWidth: number, videoDisplayHeight: number, offsetX: number, offsetY: number;

      if (canvasAspect > videoAspect) {
        // Canvas шире - letterboxing (черные полосы сверху/снизу)
        videoDisplayHeight = canvas.height;
        videoDisplayWidth = canvas.height * videoAspect;
        offsetX = (canvas.width - videoDisplayWidth) / 2;
        offsetY = 0;
      } else {
        // Canvas выше - pillarboxing (черные полосы слева/справа)
        videoDisplayWidth = canvas.width;
        videoDisplayHeight = canvas.width / videoAspect;
        offsetX = 0;
        offsetY = (canvas.height - videoDisplayHeight) / 2;
      }

      // Масштаб для конвертации координат видео в координаты canvas
      const scaleX = videoDisplayWidth / videoDimensions.width;
      const scaleY = videoDisplayHeight / videoDimensions.height;

      // Рисуем ROI только для моделей, которые всё ещё привязаны к камере
      roiModelKeys.forEach((modelName) => {
        const modelRoi = roiData[modelName];

        // Рисуем pre_crop (если есть)
        if (modelRoi.pre_crop && modelRoi.pre_crop.x !== null && modelRoi.pre_crop.y !== null) {
          const x = offsetX + (modelRoi.pre_crop.x * scaleX);
          const y = offsetY + (modelRoi.pre_crop.y * scaleY);
          const w = modelRoi.pre_crop.w * scaleX;
          const h = modelRoi.pre_crop.h * scaleY;

          // Рисуем зеленый прямоугольник с прозрачной заливкой
          ctx.strokeStyle = '#10b981';
          ctx.fillStyle = 'rgba(16, 185, 129, 0)';
          ctx.lineWidth = 3;
          ctx.setLineDash([]);
          ctx.fillRect(x, y, w, h);
          ctx.strokeRect(x, y, w, h);

          // Подпись модели (на русском, без пометки pre-crop)
          ctx.fillStyle = '#10b981';
          ctx.font = 'bold 14px Arial';
          ctx.fillText(getModelDisplayName(modelName), x + 5, y + 20);
        }

        // Рисуем post_filter (если есть)
        if (modelRoi.post_filter && Array.isArray(modelRoi.post_filter)) {
          modelRoi.post_filter.forEach((filter: any) => {
            if (filter && filter.x !== null && filter.y !== null) {
              const x = offsetX + (filter.x * scaleX);
              const y = offsetY + (filter.y * scaleY);
              const w = filter.w * scaleX;
              const h = filter.h * scaleY;

              // Рисуем зеленый прямоугольник с пунктирной рамкой
              ctx.strokeStyle = '#10b981';
              ctx.fillStyle = 'rgba(16, 185, 129, 0.15)';
              ctx.lineWidth = 2;
              ctx.setLineDash([5, 5]);
              ctx.fillRect(x, y, w, h);
              ctx.strokeRect(x, y, w, h);

              // Подпись модели (на русском, без пометки post-filter)
              ctx.fillStyle = '#10b981';
              ctx.font = 'bold 12px Arial';
              ctx.fillText(getModelDisplayName(modelName), x + 5, y + 15);
            }
          });
        }
      });

      if (hasPresenceZone && presenceZone) {
        const x = offsetX + presenceZone.x * scaleX;
        const y = offsetY + presenceZone.y * scaleY;
        const w = presenceZone.w * scaleX;
        const h = presenceZone.h * scaleY;

        ctx.strokeStyle = '#f59e0b';
        ctx.fillStyle = 'rgba(245, 158, 11, 0.15)';
        ctx.lineWidth = 3;
        ctx.setLineDash([]);
        ctx.fillRect(x, y, w, h);
        ctx.strokeRect(x, y, w, h);

        ctx.fillStyle = '#f59e0b';
        ctx.font = 'bold 14px Arial';
        ctx.fillText('Контроль присутствия', x + 5, y + 20);
      }
    };

    // Обновляем размеры при загрузке и изменении размера
    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);

    // Периодически обновляем оверлей (на случай изменения ROI)
    const intervalId = setInterval(() => {
      updateCanvasSize();
    }, 2000);

    return () => {
      window.removeEventListener('resize', updateCanvasSize);
      clearInterval(intervalId);
      clear();
    };
  }, [
    activeCamera?.status?.roi,
    attachedCameraModels,
    activeCameraId,
    videoDimensions.width,
    videoDimensions.height,
    cameras,
    presenceMonitoringEnabled,
    presenceZone,
  ]);

  // Инициализация основного видео-плеера (WebRTC через iframe)
  useEffect(() => {
    const container = mainVideoRef.current;
    if (!container || !activeCamera?.webrtc_out) return;

    let streamUrl = activeCamera.webrtc_out;
    if (streamUrl.startsWith('https://') && streamUrl.includes('192.168.')) {
      streamUrl = streamUrl.replace('https://', 'http://');
    }

    // Удаляем старый iframe если есть
    if (mainIframeRef.current && mainIframeRef.current.parentNode) {
      mainIframeRef.current.parentNode.removeChild(mainIframeRef.current);
    }

    // Создаем новый iframe
    const iframe = document.createElement('iframe');
    iframe.src = streamUrl;
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = 'none';
    iframe.style.borderRadius = '18px';
    iframe.allow = 'autoplay; fullscreen';
    iframe.setAttribute('allowfullscreen', 'true');
    mainIframeRef.current = iframe;
    container.appendChild(iframe);

    return () => {
      if (mainIframeRef.current && mainIframeRef.current.parentNode) {
        mainIframeRef.current.parentNode.removeChild(mainIframeRef.current);
      }
      mainIframeRef.current = null;
    };
  }, [activeCamera?.webrtc_out, activeCameraId, videoReloadToken]);

  const renderVideoBottomBar = () => {
    // Счётчик людей временно отключён
    // if (showPeopleCounterPanel) {
    //   return (
    //     <div className="area-cameras__video-bottom area-cameras__video-bottom--counter">
    //       <span className="area-cameras__counter-label">Счетчик людей:</span>
    //       <strong className="area-cameras__people-count-value">
    //         {peopleCount !== null ? peopleCount : '—'}
    //       </strong>
    //       {peopleWsConnected && <span className="area-cameras__live-dot" aria-hidden="true" />}
    //       {peopleWsError && (
    //         <span className="area-cameras__counter-ws-hint">нет связи</span>
    //       )}
    //     </div>
    //   );
    // }

    return (
      <div className="area-cameras__video-bottom">
        <span className="area-cameras__live-dot" aria-hidden="true" />
        <span>Живое видео</span>
      </div>
    );
  };

  const [isCameraFormOpen, setIsCameraFormOpen] = useState(false);
  const [cameraFormMode, setCameraFormMode] = useState<'add' | 'edit'>('add');
  const [editingCameraId, setEditingCameraId] = useState<number | null>(null);
  const [editingSourceId, setEditingSourceId] = useState<string | null>(null);
  const [initialEditModels, setInitialEditModels] = useState<string[]>([]);
  const [initialEditName, setInitialEditName] = useState('');
  const [initialEditRtsp, setInitialEditRtsp] = useState('');
  const [peopleCounterEnabled, setPeopleCounterEnabled] = useState(false);
  const [initialPeopleCounterEnabled, setInitialPeopleCounterEnabled] = useState(false);
  const [presenceCheckEnabled, setPresenceCheckEnabled] = useState(false);
  const [initialPresenceCheckEnabled, setInitialPresenceCheckEnabled] = useState(false);
  const [newCameraName, setNewCameraName] = useState('');
  const [newCameraRtsp, setNewCameraRtsp] = useState('');
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [isSubmittingCamera, setIsSubmittingCamera] = useState(false);
  const [cameraError, setCameraError] = useState<string>('');

  const resetCameraFormFields = () => {
    setNewCameraName('');
    setNewCameraRtsp('');
    setSelectedModels([]);
    setCameraError('');
    setCameraFormMode('add');
    setEditingCameraId(null);
    setEditingSourceId(null);
    setInitialEditModels([]);
    setInitialEditName('');
    setInitialEditRtsp('');
    setPeopleCounterEnabled(false);
    setInitialPeopleCounterEnabled(false);
    setPresenceCheckEnabled(false);
    setInitialPresenceCheckEnabled(false);
  };

  const selectedHasPeopleModel = useMemo(
    () => cameraHasPeopleCounterModel(selectedModels),
    [selectedModels]
  );

  const selectedHasPresenceModel = useMemo(
    () => cameraHasPresenceCheckModel(selectedModels),
    [selectedModels]
  );

  // Счётчик людей временно отключён — статус не запрашиваем
  // useEffect(() => {
  //   if (!activeCamera?.camera_id || !activeHasPeopleModel) {
  //     setPeopleCounterLiveEnabled(false);
  //     return;
  //   }
  //
  //   let cancelled = false;
  //   camerasAPI
  //     .getPeopleCounterStatus(activeCamera.camera_id)
  //     .then((res) => {
  //       if (!cancelled && res.success) {
  //         setPeopleCounterLiveEnabled(Boolean(res.enabled));
  //       }
  //     })
  //     .catch(() => {
  //       if (!cancelled) setPeopleCounterLiveEnabled(false);
  //     });
  //
  //   return () => {
  //     cancelled = true;
  //   };
  // }, [activeCamera?.camera_id, activeHasPeopleModel]);

  useEffect(() => {
    if (!activeCamera?.camera_id || !activeHasPresenceModel) {
      setPresenceMonitoringEnabled(false);
      setPresenceZone(null);
      return;
    }

    let cancelled = false;
    camerasAPI
      .getPresenceSettings(activeCamera.camera_id)
      .then((res) => {
        if (!cancelled && res.success) {
          const settings = normalizePresenceSettings(res.settings ?? res);
          setPresenceMonitoringEnabled(settings.enabled);
          setPresenceZone(settings.enabled ? settings.zone : null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPresenceMonitoringEnabled(false);
          setPresenceZone(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeCamera?.camera_id, activeHasPresenceModel]);

  const closeCameraFormModal = () => {
    setIsCameraFormOpen(false);
    resetCameraFormFields();
  };

  const openAddCameraModal = () => {
    resetCameraFormFields();
    setCameraFormMode('add');
    setIsCameraFormOpen(true);
  };

  const openEditCameraModal = () => {
    if (!activeCamera?.id) return;
    resetCameraFormFields();
    setCameraFormMode('edit');
    setEditingCameraId(activeCamera.id);
    setEditingSourceId(activeCamera.camera_id);
    const m = [...(activeCamera.models ?? [])];
    setInitialEditModels(m);
    setSelectedModels(m);
    const rtspForForm =
      (activeCamera.rtsp_uri && activeCamera.rtsp_uri.trim()) ||
      (typeof activeCamera.status?.rtsp_uri === 'string' ? activeCamera.status.rtsp_uri.trim() : '') ||
      '';
    setInitialEditName(activeCamera.name);
    setInitialEditRtsp(rtspForForm);
    setNewCameraName(activeCamera.name);
    setNewCameraRtsp(rtspForForm);
    setIsCameraFormOpen(true);

    // Счётчик людей временно отключён
    // const hasPeople = cameraHasPeopleCounterModel(m);
    const hasPresence = cameraHasPresenceCheckModel(m);
    const extId = activeCamera.camera_id;
    // if (extId && hasPeople) {
    //   camerasAPI
    //     .getPeopleCounterStatus(extId)
    //     .then((res) => {
    //       if (res.success) {
    //         setPeopleCounterEnabled(Boolean(res.enabled));
    //         setInitialPeopleCounterEnabled(Boolean(res.enabled));
    //       }
    //     })
    //     .catch(() => {
    //       setPeopleCounterEnabled(false);
    //       setInitialPeopleCounterEnabled(false);
    //     });
    // } else {
    setPeopleCounterEnabled(false);
    setInitialPeopleCounterEnabled(false);
    // }

    if (extId && hasPresence) {
      camerasAPI
        .getPresenceSettings(extId)
        .then((res) => {
          if (res.success) {
            const settings = normalizePresenceSettings(res.settings ?? res);
            setPresenceCheckEnabled(settings.enabled);
            setInitialPresenceCheckEnabled(settings.enabled);
          }
        })
        .catch(() => {
          setPresenceCheckEnabled(false);
          setInitialPresenceCheckEnabled(false);
        });
    } else {
      setPresenceCheckEnabled(false);
      setInitialPresenceCheckEnabled(false);
    }
  };

  const modelsEditableInForm = cameraFormMode !== 'edit' || Boolean(editingSourceId);

  // Счётчик людей временно отключён
  // const applyPeopleCounterSetting = async (externalCameraId: string, enabled: boolean) => {
  //   if (enabled) {
  //     return camerasAPI.enablePeopleCounter(externalCameraId);
  //   }
  //   return camerasAPI.disablePeopleCounter(externalCameraId);
  // };

  const applyPresenceMonitoring = async (externalCameraId: string, enabled: boolean) => {
    if (enabled) {
      return camerasAPI.setPresenceSettings(externalCameraId, {
        ...DEFAULT_PRESENCE_SETTINGS,
        enabled: true,
      });
    }
    return camerasAPI.setPresenceSettings(externalCameraId, { enabled: false });
  };

  const handleToggleModel = (modelName: string) => {
    setSelectedModels((prev) => {
      const next = prev.includes(modelName)
        ? prev.filter((m) => m !== modelName)
        : [...prev, modelName];
      if (!next.some(isPeopleCounterModel)) {
        setPeopleCounterEnabled(false);
      }
      if (!next.some(isPresenceCheckModel)) {
        setPresenceCheckEnabled(false);
      }
      return next;
    });
  };

  const handleCameraFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!area || !newCameraName.trim() || !newCameraRtsp.trim()) {
      setCameraError('Заполните все обязательные поля');
      return;
    }

    try {
      setIsSubmittingCamera(true);
      setCameraError('');

      if (cameraFormMode === 'edit' && editingCameraId != null) {
        const name = newCameraName.trim();
        const rtsp = newCameraRtsp.trim();
        const added = selectedModels.filter((m) => !initialEditModels.includes(m));
        const removed = initialEditModels.filter((m) => !selectedModels.includes(m));

        if (!editingSourceId && (added.length > 0 || removed.length > 0)) {
          setCameraError('Нельзя изменить модели: у камеры нет идентификатора (camera_id).');
          return;
        }

        const nameChanged = name !== initialEditName;
        const rtspChanged = rtsp !== initialEditRtsp;

        if (editingSourceId) {
          const patchBody: { name?: string; rtsp_uri?: string } = {};
          if (nameChanged) patchBody.name = name;
          if (rtsp) patchBody.rtsp_uri = rtsp;

          if (Object.keys(patchBody).length > 0) {
            const respPatch = await camerasAPI.patchBySourceId(editingSourceId, patchBody);
            if (!respPatch.success) {
              setCameraError(respPatch.message || 'Ошибка сохранения камеры');
              return;
            }
          }
        } else if (nameChanged || rtspChanged) {
          const respUpdate = await camerasAPI.update(editingCameraId, name, rtsp);
          if (!respUpdate.success) {
            setCameraError(respUpdate.message || 'Ошибка сохранения камеры');
            return;
          }
        }

        if (editingSourceId) {
          for (const modelName of added) {
            const r = await camerasAPI.attachModel(editingSourceId, modelName);
            if (!r.success) {
              setCameraError(r.message || `Не удалось привязать модель: ${modelName}`);
              return;
            }
          }
          for (const modelName of removed) {
            const r = await camerasAPI.detachModel(editingSourceId, modelName);
            if (!r.success) {
              setCameraError(r.message || `Не удалось отвязать модель: ${modelName}`);
              return;
            }
          }

          // Счётчик людей временно отключён
          // const stillHasPeople = cameraHasPeopleCounterModel(selectedModels);
          const stillHasPresence = cameraHasPresenceCheckModel(selectedModels);
          // if (stillHasPeople && peopleCounterEnabled !== initialPeopleCounterEnabled) {
          //   const pc = await applyPeopleCounterSetting(editingSourceId, peopleCounterEnabled);
          //   if (!pc.success) {
          //     setCameraError(pc.message || 'Не удалось изменить счётчик людей');
          //     return;
          //   }
          //   setPeopleCounterLiveEnabled(peopleCounterEnabled);
          // } else if (!stillHasPeople && initialPeopleCounterEnabled) {
          //   const pc = await camerasAPI.disablePeopleCounter(editingSourceId);
          //   if (!pc.success) {
          //     setCameraError(pc.message || 'Не удалось отключить счётчик людей');
          //     return;
          //   }
          //   setPeopleCounterLiveEnabled(false);
          // }

          if (stillHasPresence && presenceCheckEnabled !== initialPresenceCheckEnabled) {
            const pr = await applyPresenceMonitoring(editingSourceId, presenceCheckEnabled);
            if (!pr.success) {
              setCameraError(pr.message || 'Не удалось изменить проверку присутствия');
              return;
            }
            setPresenceMonitoringEnabled(presenceCheckEnabled);
          } else if (!stillHasPresence && initialPresenceCheckEnabled) {
            const pr = await camerasAPI.setPresenceSettings(editingSourceId, { enabled: false });
            if (!pr.success) {
              setCameraError(pr.message || 'Не удалось отключить проверку присутствия');
              return;
            }
            setPresenceMonitoringEnabled(false);
          }
        }

        await loadCameras();
        setVideoReloadToken((token) => token + 1);
        closeCameraFormModal();
        return;
      }

      const response = await camerasAPI.create(
        newCameraName.trim(),
        newCameraRtsp.trim(),
        area.id,
        selectedModels
      );

      if (response.success) {
        const extId = response.camera?.camera_id;
        // Счётчик людей временно отключён
        // if (extId && cameraHasPeopleCounterModel(selectedModels) && peopleCounterEnabled) {
        //   const pc = await camerasAPI.enablePeopleCounter(extId);
        //   if (!pc.success) {
        //     setCameraError(pc.message || 'Камера создана, но не удалось включить счётчик людей');
        //     await loadCameras();
        //     return;
        //   }
        //   setPeopleCounterLiveEnabled(true);
        // }
        if (extId && cameraHasPresenceCheckModel(selectedModels) && presenceCheckEnabled) {
          const pr = await camerasAPI.setPresenceSettings(extId, {
            ...DEFAULT_PRESENCE_SETTINGS,
            enabled: true,
          });
          if (!pr.success) {
            setCameraError(pr.message || 'Камера создана, но не удалось включить проверку присутствия');
            await loadCameras();
            return;
          }
          setPresenceMonitoringEnabled(true);
        }

        await loadCameras();
        setVideoReloadToken((token) => token + 1);
        closeCameraFormModal();
      } else {
        console.error('Camera creation failed:', response);
        setCameraError(response.message || 'Ошибка добавления камеры');
      }
    } catch (err: any) {
      console.error('Camera form error:', err);
      let errorMessage =
        err.message || err.response?.message || (cameraFormMode === 'edit' ? 'Ошибка сохранения камеры' : 'Ошибка добавления камеры');

      const rtspHint = err.response?.rtsp_hint as string | undefined;
      if (rtspHint) {
        errorMessage = `${errorMessage}\n\n${rtspHint}`;
      }

      setCameraError(errorMessage);
    } finally {
      setIsSubmittingCamera(false);
    }
  };

  // Показываем прелоадер только если авторизация уже прошла, но данные участка еще загружаются
  if (loading && !authLoading) {
    return <Loader fullScreen size="large" />;
  }

  // Если еще идет авторизация, ProtectedRoute покажет прелоадер
  if (authLoading) {
    return null;
  }

  if (error || !area) {
    return (
      <div className="area-page">
        <Header />
        <main className="area-main container">
          <div style={{ padding: '40px', textAlign: 'center', color: '#6c6c70' }}>
            {error || 'Участок не найден'}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="area-page">
      <Header />
      <main className="area-main container">
        <section className="area-cameras">
          <header className="area-cameras__header">
            <div className="area-cameras__title-row">
              <div className='area-cameras-title-container'>
                <h1 className="area-cameras__title">{area.name}</h1>
                {cameras.length > 0 && (
                  <select
                    className="area-cameras__select"
                    value={activeCameraId || ''}
                    onChange={(e) => {
                      setActiveCameraId(e.target.value);
                    }}
                    aria-label="Выбор камеры"
                  >
                    {cameras.map((c) => {
                      const optionValue = getCameraIdentifier(c);
                      return (
                        <option key={optionValue} value={optionValue}>
                          {c.name}
                        </option>
                      );
                    })}
                  </select>
                )}
              </div>
                
              <div className="area-cameras__controls">
                <button
                  className="area-cameras__icon-button"
                  type="button"
                  aria-label="Редактировать камеру"
                  title="Редактировать камеру"
                  onClick={() => {
                    if (!activeCamera?.id) return;
                    openEditCameraModal();
                  }}
                  disabled={!activeCamera?.id}
                >
                  <img src={`${process.env.PUBLIC_URL}/icons/edit.svg`} alt="" className="area-card__icon-image" />
                </button>
                <button
                  className="area-cameras__icon-button area-cameras__icon-button--danger"
                  type="button"
                  aria-label="Удалить камеру"
                  onClick={() => {
                    if (!activeCamera || activeCamera.id === null) return;
                    setIsDeleteCameraOpen(true);
                  }}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/icons/trash.svg`}
                    alt=""
                    className="area-card__icon-image"
                  />
                </button>

                {activeCamera && activeCamera.camera_id && attachedCameraModels.length > 0 && (
                  <button
                    className="area-cameras__icon-button area-cameras__icon-button--roi"
                    type="button"
                    onClick={() => setIsRoiSelectorOpen(true)}
                    aria-label="Выбрать зону ROI"
                    title="Выбрать зону отслеживания"
                  >
                    <img src={roiIconSvgUrl} alt="" className="area-card__icon-image" />
                  </button>
                )}

                {activeCamera &&
                  activeCamera.camera_id &&
                  activeHasPresenceModel &&
                  presenceMonitoringEnabled && (
                    <button
                      className="area-cameras__icon-button area-cameras__icon-button--presence"
                      type="button"
                      onClick={() => setIsPresenceSettingsOpen(true)}
                      aria-label="Настройки контроля присутствия"
                      title="Контроль присутствия"
                    >
                      <img src={presenceIconSvgUrl} alt="" className="area-card__icon-image" />
                    </button>
                  )}

                <button
                  className="area-cameras__add"
                  type="button"
                  onClick={openAddCameraModal}
                >
                  <span className="area-cameras__add-plus">+</span>
                  <span>Добавить камеру</span>
                </button>
              </div>
            </div>
            {area.description && (
              <p style={{ margin: '12px 0 0', fontSize: '16px', color: '#6c6c70', lineHeight: '1.5' }}>
                {area.description}
              </p>
            )}
          </header>

          {camerasLoading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6c6c70' }}>
              Загрузка камер...
            </div>
          ) : cameras.length === 0 ? (
            <div style={{
              padding: '80px 40px',
              textAlign: 'center',
              background: '#ffffff',
              borderRadius: '18px',
              boxShadow: '0 18px 45px rgba(0, 0, 0, 0.04)',
            }}>
              <div style={{ marginBottom: '16px' }}>
                <img
                  src={cameraIconSvgUrl}
                  alt=""
                  width={48}
                  height={48}
                  aria-hidden={true}
                />
              </div>
              <h2 style={{
                margin: '0 0 8px',
                fontSize: '24px',
                fontWeight: 600,
                color: '#1c1c1e',
              }}>
                Камера еще не добавлена
              </h2>
              <p style={{
                margin: 0,
                fontSize: '16px',
                color: '#6c6c70',
              }}>
                Добавьте первую камеру, чтобы начать мониторинг
              </p>
            </div>
          ) : cameras.length === 1 ? (
            // Если только одна камера - занимает полную ширину
            <div className="area-cameras__single">
              <div className="area-cameras__video" ref={mainVideoRef}>
                {!activeCamera?.webrtc_out && (
                  <div
                    className="area-cameras__video-placeholder"
                    style={{
                      backgroundImage: `url(${cameraSimUrl})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                    }}
                    aria-hidden="true"
                  />
                )}

                <canvas
                  ref={roiOverlayCanvasRef}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    pointerEvents: 'none',
                    width: '100%',
                    height: '100%',
                    zIndex: 2,
                  }}
                />

                <div className="area-cameras__video-overlay">
                  <div className="area-cameras__video-top">
                    <span className="area-cameras__video-label">{activeCamera?.name}</span>
                  </div>

                  {renderVideoBottomBar()}
                </div>
              </div>
            </div>
          ) : (
            // Если есть несколько камер - layout с основной слева и миниатюрами справа
            <div className="area-cameras__layout">
              <article className="area-cameras__main">
                <div className="area-cameras__video" ref={mainVideoRef}>
                  {!activeCamera?.webrtc_out && (
                    <div
                      className="area-cameras__video-placeholder"
                      style={{
                        backgroundImage: `url(${cameraSimUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                      }}
                      aria-hidden="true"
                    />
                  )}

                  <canvas
                    ref={roiOverlayCanvasRef}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      pointerEvents: 'none',
                      width: '100%',
                      height: '100%',
                      zIndex: 2,
                    }}
                  />

                  <div className="area-cameras__video-overlay">
                    <div className="area-cameras__video-top">
                      <span className="area-cameras__video-label">{activeCamera?.name}</span>
                    </div>

                    {renderVideoBottomBar()}
                  </div>
                </div>
              </article>

              <aside className="area-cameras__grid" aria-label="Список камер">
                {cameras.filter(c => {
                  return getCameraIdentifier(c) !== String(activeCameraId);
                }).map((c) => {
                  const isActive = getCameraIdentifier(c) === String(activeCameraId);

                  let thumbStreamUrl: string | null = c.webrtc_out ?? null;
                  if (thumbStreamUrl && thumbStreamUrl.startsWith('https://') && thumbStreamUrl.includes('192.168.')) {
                    thumbStreamUrl = thumbStreamUrl.replace('https://', 'http://');
                  }

                  return (
                    <button
                      key={getCameraIdentifier(c)}
                      className={`area-cameras__thumb ${isActive ? 'area-cameras__thumb--active' : ''}`}
                      type="button"
                      onClick={() => setActiveCameraId(getCameraIdentifier(c))}
                      aria-label={`Открыть ${c.name}`}
                    >
                      <div className="area-cameras__thumb-title area-cameras__thumb-title--top">
                        {c.name}
                      </div>
                      <div className="area-cameras__thumb-media" aria-hidden="true">
                        {thumbStreamUrl ? (
                          <iframe
                            src={thumbStreamUrl}
                            title={c.name}
                            loading="lazy"
                            allow="autoplay; fullscreen"
                            allowFullScreen
                            tabIndex={-1}
                          />
                        ) : (
                          <div
                            className="area-cameras__thumb-placeholder"
                            style={{
                              backgroundImage: `url(${cameraSimUrl})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              backgroundRepeat: 'no-repeat',
                            }}
                          />
                        )}
                      </div>
                    </button>
                  );
                })}
              </aside>
            </div>
          )}

          {isDeleteCameraOpen && activeCamera && activeCamera.id !== null && (
            <div className="area-modal" role="dialog" aria-modal="true">
              <div
                className="area-modal__backdrop"
                onClick={() => setIsDeleteCameraOpen(false)}
              />
              <div className="area-modal__dialog area-modal__dialog--small">
                <header className="area-modal__header">
                  <h2 className="area-modal__title">Удалить камеру</h2>
                  <button
                    className="area-modal__close"
                    type="button"
                    onClick={() => setIsDeleteCameraOpen(false)}
                    aria-label="Закрыть"
                  >
                    ×
                  </button>
                </header>

                <div style={{ fontSize: 16, color: '#4b5563' }}>
                  Вы уверены, что хотите удалить камеру «{activeCamera.name}»?
                </div>

                <footer className="area-modal__footer">
                  <button
                    type="button"
                    className="area-modal__submit area-modal__submit--danger"
                    onClick={async () => {
                      if (!activeCamera || activeCamera.id === null) return;

                      // Оптимистично убираем камеру из UI сразу, чтобы не было ощущения "зависло"
                      const deletedId = activeCamera.id;
                      const prevCameras = cameras;
                      const prevActiveCameraId = activeCameraId;

                      setIsDeleteCameraOpen(false);
                      setActiveCameraId(null);
                      setCameras((prev) => prev.filter((c) => c.id !== deletedId));

                      try {
                        await camerasAPI.delete(activeCamera.id);
                        await loadCameras();
                      } catch (err) {
                        console.error('Ошибка удаления камеры', err);
                        // Откат в случае ошибки
                        setCameras(prevCameras);
                        setActiveCameraId(prevActiveCameraId);
                        window.alert('Не удалось удалить камеру. Попробуйте ещё раз.');
                      }
                    }}
                  >
                    Удалить камеру
                  </button>
                </footer>
              </div>
            </div>
          )}

          {isRoiSelectorOpen && activeCamera && (
            <RoiSelector
              camera={activeCamera}
              cameraModels={attachedCameraModels}
              onClose={() => {
                setIsRoiSelectorOpen(false);
              }}
              onUpdated={async () => {
                await loadCameras();
                setVideoReloadToken((token) => token + 1);
              }}
            />
          )}

          {isPresenceSettingsOpen && activeCamera && (
            <PresenceSettingsModal
              camera={activeCamera}
              onClose={() => setIsPresenceSettingsOpen(false)}
              onUpdated={async (zone) => {
                if (presenceMonitoringEnabled) {
                  setPresenceZone(zone);
                }
                await loadCameras();
                setVideoReloadToken((token) => token + 1);
              }}
            />
          )}
        </section>

        {isDetectionsStatsOpen && area && (
          <DetectionsStatsModal
            areaId={area.id}
            areaName={area.name}
            cameraFilterId={logsCameraFilterId}
            cameraFilterName={
              logsCameraFilterId != null
                ? cameras.find((c) => c.id === logsCameraFilterId)?.name ?? null
                : null
            }
            onClose={() => setIsDetectionsStatsOpen(false)}
          />
        )}

        <section className="area-logs" aria-label="Детекции">
          <header className="area-logs__header">
            <h2 className="area-logs__title">
              Детекции <span className="area-logs__count">({logsTotal})</span>
            </h2>

            <div className="area-logs__controls" role="group" aria-label="Фильтры детекций">
              <button
                type="button"
                className="area-logs__stats-btn"
                onClick={() => setIsDetectionsStatsOpen(true)}
                aria-label="Статистика детекций за день и неделю"
              >
                <span className="area-logs__stats-btn-icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M4 19V5M10 19V9M16 19V13M22 19V7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
                <span>Статистика</span>
              </button>

              <label className="area-logs__control">
                <span className="sr-only">Камера</span>
                <select
                  className="area-logs-card__camera-select"
                  value={logsCameraFilterId === null ? '' : String(logsCameraFilterId)}
                  disabled={logsLoading}
                  onChange={(e) => {
                    const v = e.target.value;
                    setLogsCameraFilterId(v === '' ? null : Number(v));
                    setLogsModelFilter(null);
                  }}
                  aria-label="Фильтр детекций по камере"
                >
                  <option value="">Все детекции</option>
                  {cameras
                    .filter((c): c is (typeof cameras)[number] & { id: number } => typeof c.id === 'number')
                    .map((c) => (
                      <option key={c.id} value={String(c.id)}>
                        {c.name}
                      </option>
                    ))}
                </select>
              </label>

              <label className="area-logs__control">
                <span className="sr-only">Модель</span>
                <select
                  className="area-logs-card__camera-select"
                  value={logsModelFilter ?? ''}
                  disabled={logsLoading || logsModelsLoading}
                  onChange={(e) => {
                    const v = e.target.value;
                    setLogsModelFilter(v === '' ? null : v);
                  }}
                  aria-label="Фильтр детекций по модели"
                >
                  <option value="">
                    {logsModelsLoading ? 'Загрузка моделей...' : 'Все модели'}
                  </option>
                  {modelOptionsForArea.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>

              <div className="area-logs__range" role="group" aria-label="Период">
                <span className="sr-only">Период: выберите две даты в календаре</span>
                <LogsDateRangePicker
                  dateFrom={logsDateFrom}
                  dateTo={logsDateTo}
                  onChange={(from, to) => {
                    setLogsDateFrom(from);
                    setLogsDateTo(to);
                  }}
                />
              </div>
            </div>
          </header>

          {cameras.length > 0 && (
            <article className="area-logs-card">
              <div className="area-logs-card__head">
                <h3 className="area-logs-card__title">Детекции участка</h3>
              </div>

            {logsLoading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6c6c70' }}>
                Загрузка детекций...
              </div>
            ) : logs.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6c6c70' }}>
                Детекции не найдены
              </div>
            ) : (
              <div className="area-logs-table">
                <div className="area-logs-table__head" role="row">
                  <button
                    type="button"
                    className="area-logs-table__sort area-logs-table__cell area-logs-table__cell--muted"
                    onClick={() => toggleLogsSort('timestamp')}
                    aria-label={`Сортировка по времени: сейчас ${logsSort.key === 'timestamp' ? (logsSort.dir === 'asc' ? 'по возрастанию' : 'по убыванию') : 'не выбрано'}`}
                  >
                    Время
                    <span className="area-logs-table__sort-icon" aria-hidden>
                      {logsSort.key === 'timestamp' && (
                        <span
                          className={
                            'area-logs-table__chev' + (logsSort.dir === 'asc' ? ' area-logs-table__chev--up' : '')
                          }
                        >
                          <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                            <path
                              d="M5 8l5 5 5-5"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                      )}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="area-logs-table__sort area-logs-table__cell area-logs-table__cell--muted"
                    onClick={() => toggleLogsSort('model')}
                    aria-label={`Сортировка по модели: сейчас ${logsSort.key === 'model' ? (logsSort.dir === 'asc' ? 'по возрастанию' : 'по убыванию') : 'не выбрано'}`}
                  >
                    Модель
                    <span className="area-logs-table__sort-icon" aria-hidden>
                      {logsSort.key === 'model' && (
                        <span
                          className={
                            'area-logs-table__chev' + (logsSort.dir === 'asc' ? ' area-logs-table__chev--up' : '')
                          }
                        >
                          <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                            <path
                              d="M5 8l5 5 5-5"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                      )}
                    </span>
                  </button>
                  <div className="area-logs-table__cell area-logs-table__cell--muted" role="columnheader">
                    Детекция
                  </div>
                </div>

                {sortedLogs.map((log, idx) => (
                  <div key={`${log.timestamp}-${idx}`} className="area-logs-table__row" role="row">
                    <div className="area-logs-table__cell" role="cell">
                      {log.timestamp}
                    </div>
                    <div className="area-logs-table__cell" role="cell">
                      {getModelDisplayName(log.model)}
                    </div>
                    <div className="area-logs-table__cell" role="cell">
                      {log.screen_url ? (
                        <button
                          type="button"
                          className="area-logs-card__photo-btn"
                          aria-label="Просмотр"
                          onClick={() => {
                            setSelectedPhotoUrl(log.screen_url);
                            setIsPhotoModalOpen(true);
                          }}
                        >
                          <img
                            src={cameraIconSvgUrl}
                            alt=""
                            width={18}
                            height={18}
                            className="area-logs-card__photo-btn-icon"
                            aria-hidden={true}
                          />
                          <span className="area-logs-card__photo-btn-text">Просмотр</span>
                        </button>
                      ) : (
                        <span style={{ color: '#999' }}>—</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {logs.length > 0 && logs.length < logsTotal && (
              <button 
                className="area-logs-card__more" 
                type="button"
                onClick={loadMoreLogs}
                disabled={logsLoadingMore}
              >
                {logsLoadingMore ? (
                  'Загрузка...'
                ) : (
                  `Показать еще (${logsTotal - logs.length} осталось)`
                )}
              </button>
            )}
            </article>
          )}
        </section>
      </main>

      {/* Модалка просмотра фотофиксации */}
      {isPhotoModalOpen && selectedPhotoUrl && (
        <div className="area-modal" role="dialog" aria-modal="true">
          <div
            className="area-modal__backdrop"
            onClick={() => setIsPhotoModalOpen(false)}
          />
          <div className="area-modal__dialog area-modal__dialog--photo">
            <header className="area-modal__header">
              <h2 className="area-modal__title">Фотофиксация детекции</h2>
              <button
                className="area-modal__close"
                type="button"
                onClick={() => setIsPhotoModalOpen(false)}
                aria-label="Закрыть"
              >
                ×
              </button>
            </header>

            <div className="area-modal__photo-body">
              {selectedPhotoLoading && (
                <div className="area-modal__photo-status">Загрузка изображения…</div>
              )}
              {selectedPhotoError && !selectedPhotoLoading && (
                <div className="area-modal__photo-status area-modal__photo-status--error">
                  Ошибка загрузки изображения
                </div>
              )}
              {selectedPhotoBlobUrl && !selectedPhotoLoading && !selectedPhotoError && (
                <img
                  src={selectedPhotoBlobUrl}
                  alt="Фотофиксация"
                  className="area-modal__photo-img"
                  onError={onSelectedPhotoImageError}
                />
              )}
            </div>

            <footer className="area-modal__footer">
              <button
                type="button"
                className="area-modal__submit area-modal__submit--secondary"
                onClick={() => setIsPhotoModalOpen(false)}
              >
                Закрыть
              </button>
            </footer>
          </div>
        </div>
      )}

      {isCameraFormOpen && (
        <div className="area-modal" role="dialog" aria-modal="true">
          <div
            className="area-modal__backdrop"
            onClick={closeCameraFormModal}
          />
          <div className="area-modal__dialog">
            <header className="area-modal__header">
              <h2 className="area-modal__title">
                {cameraFormMode === 'edit' ? 'Редактировать камеру' : 'Добавить новую камеру'}
              </h2>
              <button
                type="button"
                className="area-modal__close"
                aria-label="Закрыть"
                onClick={closeCameraFormModal}
              >
                ×
              </button>
            </header>

            <form className="area-modal__body" onSubmit={handleCameraFormSubmit}>
              <div className="area-modal__left">
                <div className="area-modal__field">
                  <input
                    className="area-modal__input"
                    type="text"
                    placeholder="Название камеры"
                    value={newCameraName}
                    onChange={(e) => setNewCameraName(e.target.value)}
                    required
                  />
                </div>
                <div className="area-modal__field">
                  <input
                    className="area-modal__input"
                    type="url"
                    placeholder="RTSP URL камеры"
                    value={newCameraRtsp}
                    onChange={(e) => setNewCameraRtsp(e.target.value)}
                    required
                  />
                </div>
                <div className="area-modal__field">
                  <input
                    className="area-modal__input"
                    type="text"
                    placeholder="Участок"
                    value={area?.name || ''}
                    disabled
                    style={{ backgroundColor: '#f5f5f7', cursor: 'not-allowed' }}
                  />
                </div>
                {(selectedHasPeopleModel || selectedHasPresenceModel) && (
                  <div className="area-modal__people-options">
                    {/* Счётчик людей временно отключён
                    {selectedHasPeopleModel && (
                      <label className="area-modal__people-counter-option">
                        <input
                          type="checkbox"
                          checked={peopleCounterEnabled}
                          onChange={(e) => setPeopleCounterEnabled(e.target.checked)}
                        />
                        <span className="area-modal-analytic__checkbox" aria-hidden="true" />
                        <span>Включить счётчик людей</span>
                      </label>
                    )}
                    */}
                    {selectedHasPresenceModel && (
                      <label className="area-modal__people-counter-option">
                        <input
                          type="checkbox"
                          checked={presenceCheckEnabled}
                          onChange={(e) => setPresenceCheckEnabled(e.target.checked)}
                        />
                        <span className="area-modal-analytic__checkbox" aria-hidden="true" />
                        <span>Проверка присутствия</span>
                      </label>
                    )}
                  </div>
                )}
                {cameraFormMode === 'edit' && !editingSourceId && (
                  <p style={{ margin: 0, fontSize: '14px', color: '#6c6c70', lineHeight: 1.45 }}>
                    Изменение списка моделей недоступно: у камеры нет идентификатора источника.
                  </p>
                )}
              </div>

              <div className="area-modal__right">
                <h3 className="area-modal__subtitle">Аналитики:</h3>

                <div className="area-modal__analytics-list">
                  {modelsLoading ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#6c6c70' }}>
                      Загрузка моделей...
                    </div>
                  ) : availableModels.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#6c6c70' }}>
                      Нет доступных моделей
                    </div>
                  ) : (
                    availableModels.map((model) => {
                      // Определяем иконку по названию модели
                      let icon = 'lock.svg';
                      const modelKey = (model || '').trim().toLowerCase();

                      if (modelKey.includes('license_plate') || modelKey.includes('plate')) {
                        // Распознавание номеров
                        icon = 'lock.svg';
                      } else if (modelKey === 'fire_and_smoke_detector') {
                        icon = 'fire-smoke.svg';
                      } else if (modelKey === 'fire') {
                        icon = 'fire-smoke.svg';
                      } else if (modelKey === 'smoke') {
                        icon = 'smoke.svg';
                      } else if (
                        modelKey.includes('multiple_rtsp') ||
                        ['car', 'vehicle', 'bus', 'truck', 'police', 'ambulance', 'firetruck', 'emergency_car_detector'].includes(
                          modelKey
                        )
                      ) {
                        // Машины
                        icon = 'car.svg';
                      } else if (modelKey === 'people' || modelKey === 'person' || modelKey === 'utrau_staff_detector') {
                        icon = 'people.svg';
                      } else if (
                        modelKey === 'smoking' ||
                        modelKey === 'smoking_detector' ||
                        modelKey === 'smoking_detector_v2' ||
                        modelKey === 'cigarette'
                      ) {
                        icon = 'kyrenie.svg';
                      } else if (modelKey === 'garbage_truck_detector' || modelKey === 'garbage_truck') {
                        icon = 'mysor.svg';
                      } else if (modelKey === 'tractor_detector') {
                        icon = 'tractor.svg';
                      }

                      // Выводим переведенное название модели
                      const label = getModelDisplayName(model);

                      return (
                        <label key={model} className="area-modal-analytic">
                          <span className="area-modal-analytic__icon">
                            <img src={`${modalIconsBase}/${icon}`} alt="" />
                          </span>
                          <span className="area-modal-analytic__text">{label}</span>
                          <input
                            type="checkbox"
                            checked={selectedModels.includes(model)}
                            disabled={!modelsEditableInForm}
                            onChange={() => handleToggleModel(model)}
                          />
                          <span className="area-modal-analytic__checkbox" aria-hidden="true" />
                        </label>
                      );
                    })
                  )}
                </div>
              </div>

              {cameraError && (
                <div style={{ gridColumn: '1 / -1', color: '#ff4444', padding: '10px', marginTop: '10px', fontSize: '14px' }}>
                  {cameraError}
                </div>
              )}

              <footer className="area-modal__footer">
                <button 
                  type="submit" 
                  className="area-modal__submit"
                  disabled={isSubmittingCamera || modelsLoading}
                >
                  <span className="area-modal__submit-plus">+</span>
                  <span>
                    {isSubmittingCamera
                      ? cameraFormMode === 'edit'
                        ? 'Сохранение...'
                        : 'Добавление...'
                      : cameraFormMode === 'edit'
                        ? 'Сохранить'
                        : 'Добавить камеру'}
                  </span>
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Area;

